import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import update from 'immutability-helper';
import 'devbridge-autocomplete/dist/jquery.autocomplete';

import Loading from '../../../../shared/loading';
import ErrorBoundary from '../../../../shared/error-boundary';
import defaultOnErrorHandler from '../../../../shared/default-on-error-handler';
import Validator from '../../../../shared/validator';
import { editorValidate } from '../channel-editor-postal';
import create from '../../../../services/keywords/create-service';
import { getWireKws, addKw, removeKw } from '../../../../services/wire/edit-keywords-service';

const ERR_TIMEOUT_SEC = 5;
const MIN_LENGTH = 3;
const MAX_LENGTH = 64;

export default class KeywordsControl extends React.Component {
  static propTypes = {
    wireId: PropTypes.number.isRequired,
  }

  constructor(props, context) {
    super(props, context);

    this.onErrorTimeout = null;
    this.validator = new Validator({
      maxKeywords: state => {
        const len = state.keywords.length;
        const isValid = len <= 5 && (len < 5 || !state.newKeyword.length);
        const op = isValid ? 'enable' : 'disable';
        this.$newKeywordInput && this.$newKeywordInput.devbridgeAutocomplete()[op]();
        isValid || this.resetError(state.newKeyword, 'maxKeywords');
        return isValid;
      },
      newKeywordMinLength: state => {
        const maxKeywordsReached = state.keywords.length >= 5;
        const suggesting = !!state.suggestionKeywords.length;
        const notInputting = !state.newKeyword.length;
        const isValid = maxKeywordsReached
          || suggesting
          || notInputting
          || state.newKeyword.length >= MIN_LENGTH;
        isValid || this.resetError(state.newKeyword, 'newKeywordMinLength');
        return isValid;
      },
      newKeywordMaxLength: state => {
        const maxKeywordsReached = state.keywords.length >= 5;
        const suggesting = !!state.suggestionKeywords.length;
        const isValid = maxKeywordsReached || suggesting || state.newKeyword.length <= MAX_LENGTH;
        isValid || this.resetError(state.newKeyword, 'newKeywordMaxLength');
        return isValid;
      },
      notStockCode: state => {
        const maxKeywordsReached = state.keywords.length >= 5;
        const suggesting = !!state.suggestionKeywords.length;
        const isValid = maxKeywordsReached || suggesting || !state.newKeyword.match(/(^asx|:asx)/i);
        isValid || this.resetError(state.newKeyword, 'notStockCode');
        return isValid;
      },
    });

    this.state = {
      keywords: [],
      suggestionKeywords: [],
      newKeyword: '',
      validations: this.validator.getValidations(),
    };

    editorValidate(this, 'keywords');
  }

  componentDidMount() {
    this.loading.start();
    getWireKws(this.props.wireId)
      .then(result => this.setState(
        state => {
          state.keywords = _.get(result, 'data.keywords', []);
          state.validations = this.validator.validateForm(state);
          return state;
        },
        () => {
          if (this.state.keywords.length >= 5) {
            this.$newKeywordInput.devbridgeAutocomplete().disable();
          }
        },
      ))
      .catch(e => defaultOnErrorHandler(e, 'Error getting keywords'))
      .finally(() => this.loading.stop());

    this.$newKeywordInput
      .devbridgeAutocomplete({
        serviceUrl: '/keywords/suggest',
        noCache: true,
        tabDisabled: true,
        triggerSelectOnValidInput: false,
        transformResult: response => {
          const suggestionKeywords = $.parseJSON(response).results;

          this.setState({ suggestionKeywords });

          return {
            suggestions: $.map(suggestionKeywords, keyword => ({
              id: keyword.id,
              value: keyword.value,
              slug: keyword.slug,
              data: { category: 'Select a Keyword' },
            })),
          };
        },
        onSearchComplete: () => {
          this.setState(state => {
            state.validations = this.validator.validateForm(state);
            return state;
          });
        },
        onSelect: keyword => {
          if (this.state.validations.formValid) {
            this.setState({ newKeyword: '' });
            this.addKeyword(keyword.value);
          }
        },
        maxHeight: 350,
        groupBy: 'category',
      })
      .on('keypress keydown', e => {
        if (e.which === 13 || e.which === 9) {
          e.preventDefault();
        }
      })
      .on('keyup', e => {
        const eligible = (e.which === 13 || e.which === 188 || e.which === 9)
          && this.state.validations.formValid
          && !(this.$newKeywordInput.devbridgeAutocomplete().disabled);
        if (eligible) {
          this.getAndAddKeywords();
        }
      });
  }

  handleNewKeywordChange(e) {
    const newKeyword = e.target.value;

    this.setState(state => {
      state.newKeyword = newKeyword;
      state.validations = this.validator.validateForm(state);
      return state;
    });
  }

  getAndAddKeywords() {
    this.state.newKeyword
      .split(',')
      .map(x => x.trim())
      .filter(x => !!x)
      .forEach(x => this.addKeyword(x));
  }

  resetError(lastNewKeyword, rule) {
    clearTimeout(this.onErrorTimeout);
    this.onErrorTimeout = setTimeout(() => {
      if (this.state.newKeyword === lastNewKeyword && !this.state.validations[`${rule}Valid`]) {
        this.validator.resetValidation();
        this.setState({ validations: this.validator.getValidations() });
      }
    }, ERR_TIMEOUT_SEC * 1000);
  }

  addKeyword(keywordValue) {
    const foundKeyword = _.find(this.state.suggestionKeywords, ['value', keywordValue]);

    if (foundKeyword) {
      if (_.some(this.state.keywords, ['id', foundKeyword.id])) return;

      this.state.validations = this.validator.validateForm(this.state);

      if (this.state.validations.formValid) {
        this.loading.start();
        addKw(this.props.wireId, foundKeyword.slug)
          .then(result => this.setState(state => update(state, {
            keywords: { $push: [result.data] },
          })))
          .catch(e => defaultOnErrorHandler(e, 'Error adding keyword'))
          .finally(() => this.loading.stop());

        this.setState({ newKeyword: '' });
        this.$newKeywordInput.val('').focus();
      }
    } else {
      this.createAndAddNewKeyword(keywordValue);
    }
  }

  createAndAddNewKeyword(keywordValue) {
    create(keywordValue)
      .then(result => {
        this.setState(state => update(state, {
          suggestionKeywords: { $push: [result.data] },
        }));
        this.addKeyword(keywordValue);
      })
      .catch(e => defaultOnErrorHandler(e, 'Error creating new keyword'))
      .finally(() => this.loading.stop());
  }

  removeKeyword(evt, keyword) {
    evt && evt.preventDefault && evt.preventDefault();

    this.setState(state => {
      state.keywords = _.filter(state.keywords, x => x !== keyword);
      state.validations = this.validator.validateForm(state);
      return state;
    });

    this.loading.start();
    removeKw(this.props.wireId, keyword.slug)
      .catch(e => defaultOnErrorHandler(e, 'Error removing keyword'))
      .finally(() => this.loading.stop());
  }

  render() {
    return (
      <ErrorBoundary>
        <aside className="sidebar-item">
          <Loading variant="inline" ref={el => (this.loading = el)}>
            <section className="form-group">
              <p className={`form-group ${this.state.validations.formValid ? '' : 'has-error'}`}>
                <input
                  type="text"
                  className="sidebar-item-input-autocomplete"
                  value={this.state.newKeyword}
                  ref={el => (this.$newKeywordInput = $(el))}
                  onChange={e => this.handleNewKeywordChange(e)}
                  placeholder="Enter Keyword (Max 5)"
                />
                {this.state.validations.maxKeywordsValid || <span className="help-block">You can only use max 5 keywords. Hover a keyword below to delete.</span>}
                {this.state.validations.newKeywordMinLengthValid || <span className="help-block">You cannot use a keyword shorter than 3 characters.</span>}
                {this.state.validations.newKeywordMaxLengthValid || <span className="help-block">You cannot use a keyword longer than 64 characters.</span>}
                {this.state.validations.notStockCodeValid || <span className="help-block">You cannot use a stock code as a keyword.</span>}
              </p>
              {!!this.state.keywords.length && (
                <ul className="sidebar-item-ulist keywords-container">
                  {this.state.keywords.map(keyword => (
                    <li
                      className="sidebar-item-list-item keyword-item"
                      key={keyword.id}
                      onClick={e => this.removeKeyword(e, keyword)}
                    >
                      {keyword.value}
                      <span className="visible-on-hover">&times;</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </Loading>
        </aside>
      </ErrorBoundary>
    );
  }
}
